/**
 * SparePartReviewQueue — admin page to resolve LLM-backfilled low-confidence
 * spare-part parent matches.
 *
 * @useWhen Operator needs to clean up after a backfill run; LLM matches with
 *   confidence < 0.85 land in spare_part_review_queue (status='pending') and
 *   are surfaced here for human review.
 * @dontUseFor Normal spare-part creation — use the PDP CTA or regular wizard.
 * @related ProductSearchSelect (Pick Other... uses it)
 * @reuseHints The "pending review" pattern (LLM suggestion + 3-action resolver)
 *   could be repeated for other LLM-assisted classification flows.
 */
import React, { useEffect, useState } from 'react';
import {
  Box, Typography, Table, TableHead, TableBody, TableRow, TableCell,
  Chip, Button, Stack, Alert,
  Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions,
} from '@mui/material';
import axios from 'axios';
import { toast } from 'sonner';
import TableViewport from '../components/common/TableViewport';
import DisabledReason from '../components/common/DisabledReason';
import HoldButton from '../components/common/confirm/HoldButton';
import TableSkeleton from '../Skeletons/TableSkeleton';

const API = process.env.REACT_APP_API_BASE_URL;

// Admin header pattern — mirror the existing ProductTaxonomyDetail.jsx ADMIN_HEADERS
const ADMIN_HEADERS = {
  headers: { 'X-Admin-Key': process.env.REACT_APP_ADMIN_KEY || '' },
};

export default function SparePartReviewQueue() {
  const [queue, setQueue] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    axios.get(`${API}/api/products/admin/spare-parts-review-queue`, ADMIN_HEADERS)
      .then((res) => {
        if (cancelled) return;
        setQueue(res.data?.items || []);
        setLoading(false);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err.message || 'Failed to load review queue');
        setLoading(false);
      });
    return () => { cancelled = true; };
  }, []);

  if (loading) return <ReviewQueueSkeleton />;
  if (error) return <Alert severity="error">{error}</Alert>;

  // Kopf und Einzug kommen von `AdminLayout` (Abschnittsname als `h1`,
  // Brotkrume „Admin › …", Tab-Leiste, `PageContainer`) — die Seite rendert
  // nur ihren Inhalt.
  return (
    <>
      <Typography variant="body2" color="text.secondary" mb={2}>
        Low-confidence LLM matches from the backfill run. Pick the correct parent,
        choose a different one, or mark this spare part as universal (no parent).
      </Typography>

      {queue.length === 0 ? (
        <Alert severity="success">Queue is empty. All LLM matches resolved.</Alert>
      ) : (
        <TableViewport>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Spare part</TableCell>
                <TableCell>Top candidate</TableCell>
                <TableCell>Confidence</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {queue.map((row) => (
                <ReviewRow key={row.id} row={row} />
              ))}
            </TableBody>
          </Table>
        </TableViewport>
      )}

      <Box sx={{ mt: 6 }}>
        <SuspiciousLinksSection />
      </Box>
    </>
  );
}

/**
 * SuspiciousLinksSection — review queue for accessories wrongly linked as
 * spare parts (e.g. a bundled cleaning brush auto-matched to a machine).
 * Human resolves each: unlink the bad association, or confirm it really is
 * a spare part via a taxonomy override. No blind delete.
 */
function SuspiciousLinksSection() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);
  // Unlink is destructive — confirm via Dialog + HoldButton (repo pattern,
  // see PODetail.jsx confirmDialog) rather than firing the DELETE on click.
  const [unlinkTarget, setUnlinkTarget] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    axios.get(`${API}/api/products/admin/spare-parts-suspicious-links`, ADMIN_HEADERS)
      .then((res) => {
        if (cancelled) return;
        setItems(res.data?.items || []);
        setLoading(false);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err.message || 'Failed to load suspicious links');
        setLoading(false);
      });
    return () => { cancelled = true; };
  }, [refreshKey]);

  const refetch = () => setRefreshKey((k) => k + 1);
  const closeUnlinkDialog = () => setUnlinkTarget(null);

  const confirmUnlink = async () => {
    if (!unlinkTarget) return;
    try {
      // Unlink endpoint is not admin-gated — send no headers.
      await axios.delete(`${API}/api/products/${unlinkTarget.parent_sku}/spare-parts/${unlinkTarget.spare_sku}`);
      toast.success('Link removed');
      closeUnlinkDialog();
      refetch();
    } catch (err) {
      console.error('[SparePartReviewQueue] unlink failed', err);
      toast.error('Failed to unlink: ' + (err.message || 'unknown'));
    }
  };

  if (loading) {
    // K-S (UI-Durchgang 2): der Seitenaufbau wartet als Geruest in der Form der
    // geladenen Tabelle (4 Spalten), nicht als Kreisel (anti-patterns §11).
    return (
      <TableViewport>
        <TableSkeleton framed={false} columns={4} rows={5} ariaLabel="Loading suspicious spare-part links" />
      </TableViewport>
    );
  }
  if (error) return <Alert severity="error">{error}</Alert>;

  return (
    <>
      <Typography variant="h6" mb={1}>Suspicious Spare-Part Links</Typography>
      <Typography variant="body2" color="text.secondary" mb={2}>
        Accessories that were wrongly linked as spare parts. Unlink the bad
        association, or confirm it really is a spare part.
      </Typography>

      {items.length === 0 ? (
        <Alert severity="success">No suspicious spare-part links.</Alert>
      ) : (
        <TableViewport>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Spare part</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Parent SKU</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {items.map((row) => (
                <SuspiciousLinkRow
                  key={`${row.parent_sku}-${row.spare_sku}`}
                  row={row}
                  onResolved={refetch}
                  onRequestUnlink={() => setUnlinkTarget(row)}
                />
              ))}
            </TableBody>
          </Table>
        </TableViewport>
      )}

      <Dialog open={!!unlinkTarget} onClose={closeUnlinkDialog} aria-labelledby="unlink-dialog-title">
        <DialogTitle id="unlink-dialog-title">Unlink spare part?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This removes the spare-part link between{' '}
            <strong>{unlinkTarget?.spare_name}</strong> ({unlinkTarget?.spare_sku}) and
            parent SKU <strong>{unlinkTarget?.parent_sku}</strong>. Neither product is deleted.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={closeUnlinkDialog}>Cancel</Button>
          <HoldButton
            holdMs={900}
            label="Hold to unlink"
            labelActive="Keep holding…"
            labelDone="Unlinked"
            color="error"
            onConfirm={confirmUnlink}
          />
        </DialogActions>
      </Dialog>
    </>
  );
}

function SuspiciousLinkRow({ row, onResolved, onRequestUnlink }) {
  const [busy, setBusy] = useState(false);

  const handleConfirmSparePart = async () => {
    setBusy(true);
    try {
      await axios.post(
        `${API}/api/admin/taxonomy/overrides`,
        { product_id: row.spare_product_id, equipment_category: 'spare_part' },
        ADMIN_HEADERS,
      );
      toast.success('Marked as spare part');
      onResolved();
    } catch (err) {
      console.error('[SparePartReviewQueue] taxonomy override failed', err);
      toast.error('Failed to mark as spare part: '
        + (err?.response?.data?.hinweis || err.message || 'unknown'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <TableRow>
      <TableCell>
        <Stack>
          <Typography variant="body2" fontWeight={500}>{row.spare_name}</Typography>
          <Typography variant="caption" color="text.secondary">
            {row.spare_variant ? `${row.spare_sku} — ${row.spare_variant}` : row.spare_sku}
          </Typography>
        </Stack>
      </TableCell>
      <TableCell>
        <Chip label={row.canonical_type} size="small" />
      </TableCell>
      <TableCell>
        <Typography variant="body2">{row.parent_sku}</Typography>
      </TableCell>
      <TableCell>
        <Stack direction="row" spacing={1}>
          <Button size="small" color="error" disabled={busy} onClick={onRequestUnlink}>
            Unlink
          </Button>
          <Button size="small" disabled={busy} onClick={handleConfirmSparePart}>
            Mark as spare part
          </Button>
        </Stack>
      </TableCell>
    </TableRow>
  );
}

function ReviewRow({ row }) {
  const [busy, setBusy] = useState(false);
  const topCandidate = (row.candidate_parents || [])[0] || null;

  const resolve = async (action, parentSku) => {
    setBusy(true);
    try {
      await axios.post(
        `${API}/api/products/admin/spare-parts-review-queue/${row.id}/resolve`,
        { action, ...(parentSku ? { parent_sku: parentSku } : {}) },
        ADMIN_HEADERS,
      );
      window.location.reload();
    } catch (err) {
      console.error('[SparePartReviewQueue] resolve failed', err);
      toast.error('Failed to resolve: ' + (err.message || 'unknown'));
      setBusy(false);
    }
  };

  const handlePick = () => {
    if (!topCandidate) return;
    resolve('pick', topCandidate.sku);
  };

  const handleUniversal = () => {
    resolve('universal');
  };

  return (
    <TableRow>
      <TableCell>
        <Stack>
          <Typography variant="body2" fontWeight={500}>{row.spare_name}</Typography>
          <Typography variant="caption" color="text.secondary">{row.spare_sku}</Typography>
        </Stack>
      </TableCell>
      <TableCell>
        {topCandidate ? (
          <Stack>
            <Typography variant="body2">{topCandidate.product_name}</Typography>
            <Typography variant="caption" color="text.secondary">{topCandidate.sku}</Typography>
          </Stack>
        ) : <Typography variant="caption" color="text.secondary">No suggestion</Typography>}
      </TableCell>
      <TableCell>
        {topCandidate && (
          <Chip
            label={`${Math.round((topCandidate.confidence || 0) * 100)}%`}
            size="small"
            color={topCandidate.confidence >= 0.7 ? 'success' : 'default'}
          />
        )}
      </TableCell>
      <TableCell>
        <Stack direction="row" spacing={1}>
          <DisabledReason
            reason={busy
              ? null
              : !topCandidate ? 'No suggested spare part for this row.' : null}
          >
            <Button
              size="small"
              disabled={busy || !topCandidate}
              onClick={handlePick}
            >
              Pick
            </Button>
          </DisabledReason>
          <Button size="small" disabled>Other&hellip;</Button>
          <Button
            size="small"
            disabled={busy}
            onClick={handleUniversal}
          >
            Universal
          </Button>
        </Stack>
      </TableCell>
    </TableRow>
  );
}

function ReviewQueueSkeleton() {
  // Rule: no spinners — render skeleton matching content shape.
  return (
    <Box>
      <Box sx={{ height: 32, width: 320, mb: 2, bgcolor: 'grey.200', borderRadius: 1 }} />
      <Box sx={{ height: 20, width: '60%', mb: 4, bgcolor: 'grey.100', borderRadius: 1 }} />
      {Array.from({ length: 4 }).map((_, i) => (
        <Box key={i} sx={{ height: 52, mb: 1, bgcolor: 'grey.50', borderRadius: 1 }} />
      ))}
    </Box>
  );
}
